import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Move, Plus, Trash2 } from 'lucide-react';
import { Category } from '../../../types/index.ts';
import { CategoryIcon } from '../../../pages/Inventory.tsx';
import apiClient, { api } from '../../../api/client.ts';
import { spinIconOut } from '../../../utils/iconAnimations.ts';
import { resolveCategoryColor } from './useCategoryColors.ts';
import { IconColorPopover } from './IconColorPopover.tsx';
import { MoveCategoryPopover } from './MoveCategoryPopover.tsx';
import Toast, { useToast } from '../../Toast.tsx';
// ParentCategoryPicker is no longer used here (replaced by MoveCategoryPopover) -- grepped the
// whole client tree, this editor was its only consumer. Left un-deleted per instructions.

// Rolled-up item count: this category's own direct item_count (from the server) plus every
// descendant's, computed client-side since the server only counts direct items per row.
function rolledUpItemCount(cat: Category, categories: Category[]): number {
  const children = categories.filter((c) => c.parent_id === cat.id);
  return (cat.item_count ?? 0) + children.reduce((sum, child) => sum + rolledUpItemCount(child, categories), 0);
}

function allDescendantIds(catId: number, categories: Category[]): Set<number> {
  const ids = new Set<number>();
  const queue = [catId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const c of categories) {
      if (c.parent_id === id && !ids.has(c.id)) { ids.add(c.id); queue.push(c.id); }
    }
  }
  return ids;
}

export function CategoryEditor({
  category,
  categories,
  categoryColorMap,
  readOnly,
  onSelect,
  onChanged,
  onDeleted,
}: {
  category: Category;
  categories: Category[];
  categoryColorMap: Map<number, string>;
  readOnly: boolean;
  onSelect: (id: number) => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(category.name);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteMoveTo, setDeleteMoveTo] = useState<number | 'uncategorized'>(category.parent_id ?? 'uncategorized');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [addingSub, setAddingSub] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tileRef = useRef<HTMLButtonElement>(null);
  const moveBtnRef = useRef<HTMLButtonElement>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast, showToast } = useToast();

  useEffect(() => { setName(category.name); }, [category.id, category.name]);
  useEffect(() => {
    setDeleteMoveTo(category.parent_id ?? 'uncategorized');
    setDeleteOpen(false);
    setDeleteConfirmText('');
    setMoveOpen(false);
  }, [category.id, category.parent_id]);
  useEffect(() => () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current); }, []);

  function flashSaved() {
    setSaved(true);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSaved(false), 1500);
  }

  async function patch(payload: Record<string, unknown>) {
    setError(null);
    try {
      await api.put(`/inventory/categories/${category.id}`, {
        name: payload.name ?? category.name,
        parent_id: payload.parent_id !== undefined ? payload.parent_id : category.parent_id,
        icon_path: payload.icon_path !== undefined ? payload.icon_path : category.icon_path,
        color: payload.color !== undefined ? payload.color : category.color,
      });
      flashSaved();
      onChanged();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Save failed';
      setError(msg);
      setName(category.name); // revert
    }
  }

  function handleNameCommit() {
    const titleCased = name.trim().replace(/\b\w/g, (c) => c.toUpperCase());
    if (!titleCased || titleCased === category.name) { setName(category.name); return; }
    setName(titleCased);
    void patch({ name: titleCased });
  }

  const color = resolveCategoryColor(category, categoryColorMap);
  const parent = category.parent_id ? categories.find((c) => c.id === category.parent_id) ?? null : null;
  const parentColor = parent ? resolveCategoryColor(parent, categoryColorMap) : undefined;
  const children = categories.filter((c) => c.parent_id === category.id);
  const totalItems = rolledUpItemCount(category, categories);
  const directItems = category.item_count ?? 0;
  const subtreeItems = totalItems - directItems; // items inside subcategories, not this category itself
  const hasItemsOrSubs = directItems > 0 || children.length > 0;

  // Breadcrumb: ancestors oldest-first, ending at this category.
  const breadcrumb: Category[] = [];
  let cur: Category | null = category;
  while (cur) { breadcrumb.unshift(cur); cur = cur.parent_id ? categories.find((c) => c.id === cur!.parent_id) ?? null : null; }

  const metaLine = parent
    ? `Inside ${parent.name} · ${children.length} subcategor${children.length === 1 ? 'y' : 'ies'} · ${totalItems} item${totalItems === 1 ? '' : 's'}`
    : `Top-level category · ${children.length} subcategor${children.length === 1 ? 'y' : 'ies'} · ${totalItems} item${totalItems === 1 ? '' : 's'}`;

  async function handleAddSub() {
    if (!newSubName.trim() || addingSub) return;
    setAddingSub(true);
    try {
      // /inventory/suggest-icons returns a bare { suggestions } body, not this app's usual
      // { success, data } envelope -- apiClient (raw axios) here, not the api.* wrapper helpers
      // (which assume that envelope and would read undefined). Same reasoning as
      // IconColorPopover.tsx's own two calls to the sibling suggest-icons-ai endpoint.
      let icon: string | undefined;
      try {
        const sugRes = await apiClient.post<{ suggestions: Array<{ name: string }> }>('/inventory/suggest-icons', { categoryName: newSubName.trim() });
        icon = sugRes.data.suggestions?.[0]?.name;
      } catch { /* no icon suggestion -- not fatal */ }
      await api.post('/inventory/categories', {
        name: newSubName.trim(),
        parent_id: category.id,
        color,
        icon_path: icon,
      });
      setNewSubName('');
      onChanged();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Could not add subcategory';
      setError(msg);
    } finally {
      setAddingSub(false);
    }
  }

  async function handleDeleteChild(child: Category) {
    try {
      await api.delete(`/inventory/categories/${child.id}?force=true`);
      onChanged();
    } catch { /* silent, matches prior behavior */ }
  }

  async function handleMove(newParentId: number | null) {
    setMoveOpen(false);
    await patch({ parent_id: newParentId });
  }

  async function confirmDelete() {
    setDeleting(true);
    setError(null);
    try {
      const qs = deleteMoveTo === 'uncategorized' ? 'force=true' : `moveTo=${deleteMoveTo}`;
      // DELETE /categories/:id replies { success, message, movedItems } -- movedItems sits at the
      // top level, not inside a `data` envelope, so this uses raw apiClient rather than api.delete.
      const res = await apiClient.delete<{ success: boolean; message?: string; movedItems?: number }>(`/inventory/categories/${category.id}?${qs}`);
      setDeleteOpen(false);
      setDeleteConfirmText('');
      const moved = res.data?.movedItems ?? directItems;
      const destinationName = deleteMoveTo === 'uncategorized' ? 'Uncategorized' : categories.find((c) => c.id === deleteMoveTo)?.name ?? 'Uncategorized';
      onDeleted();
      onChanged();
      showToast(moved > 0 ? `Deleted ${category.name}. ${moved} item${moved === 1 ? '' : 's'} moved to ${destinationName}.` : `Deleted ${category.name}.`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Delete failed';
      setError(msg);
    } finally {
      setDeleting(false);
    }
  }

  const excludedForDestination = allDescendantIds(category.id, categories);
  excludedForDestination.add(category.id);
  const destinationOptions = categories.filter((c) => !excludedForDestination.has(c.id));
  function destinationDepth(c: Category): number {
    let d = 0;
    let p: Category | undefined = c;
    while (p?.parent_id) { d++; p = categories.find((x) => x.id === p!.parent_id); }
    return d;
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Breadcrumb */}
      <div style={{ padding: '14px 24px 0', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
        {breadcrumb.map((b, i) => (
          <span key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {i > 0 && <ChevronRight size={11} />}
            <span
              onClick={() => onSelect(b.id)}
              style={{ cursor: 'pointer', color: i === breadcrumb.length - 1 ? 'var(--text-secondary)' : 'var(--text-muted)', fontWeight: i === breadcrumb.length - 1 ? 600 : 400 }}
            >
              {b.name}
            </span>
          </span>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20, position: 'relative' }}>
          <button
            ref={tileRef}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && setPopoverOpen((o) => !o)}
            style={{
              width: 68, height: 68, borderRadius: 16, flexShrink: 0, padding: 0,
              background: popoverOpen ? color : `${color}24`,
              border: `2px solid ${color}55`,
              color: popoverOpen ? '#fff' : color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: readOnly ? 'default' : 'pointer',
            }}
          >
            <CategoryIcon iconName={category.icon_path} size={30} color="currentColor" />
          </button>
          {popoverOpen && (
            <IconColorPopover
              anchorRef={tileRef}
              categoryName={category.name}
              currentIconPath={category.icon_path}
              currentColor={color}
              isNested={!!parent}
              parentColor={parentColor}
              readOnly={readOnly}
              onPickIcon={(iconPath) => void patch({ icon_path: iconPath })}
              onPickColor={(hex) => void patch({ color: hex })}
              onClear={() => void patch({ icon_path: null })}
              onClose={() => setPopoverOpen(false)}
            />
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>
              {parent ? 'Subcategory name' : 'Category name'}
            </div>
            <input
              type="text"
              value={name}
              disabled={readOnly}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameCommit}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
              style={{
                width: '100%', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)',
                background: 'none', border: 'none', outline: 'none', padding: 0, marginBottom: 6,
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              <span>{metaLine}</span>
              {saved && <span style={{ color: 'var(--accent-success)' }}>Saved</span>}
            </div>
            {error && <div style={{ fontSize: 12, color: 'var(--accent-danger)', marginTop: 4 }}>{error}</div>}
          </div>
        </div>

        {/* Subcategories section */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
            Subcategories of {category.name}
          </div>

          {!readOnly && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                placeholder="Name a new subcategory, press Enter"
                value={newSubName}
                onChange={(e) => setNewSubName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAddSub(); } }}
                className="form-input"
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn-primary combo1" disabled={!newSubName.trim() || addingSub} onClick={() => void handleAddSub()} onMouseLeave={spinIconOut}>
                <Plus size={14} className="btn-icon" /> Add subcategory
              </button>
            </div>
          )}

          {children.length === 0 ? (
            <div style={{ padding: '16px 0', color: 'var(--text-muted)', fontSize: 13 }}>
              No subcategories yet. Name one above to create it inside {category.name}.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden' }}>
              {children.map((child, i) => {
                const childColor = resolveCategoryColor(child, categoryColorMap);
                const grandchildren = categories.filter((c) => c.parent_id === child.id);
                const childMeta = grandchildren.length > 0
                  ? `${grandchildren.length} subcategor${grandchildren.length === 1 ? 'y' : 'ies'} · ${rolledUpItemCount(child, categories)} items`
                  : `${rolledUpItemCount(child, categories)} items`;
                return (
                  <div
                    key={child.id}
                    className="cat-editor-child-row"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, height: 50, padding: '0 14px',
                      borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none',
                    }}
                  >
                    <div style={{ width: 28, height: 28, borderRadius: 7, flexShrink: 0, background: `${childColor}24`, color: childColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CategoryIcon iconName={child.icon_path} size={15} color="currentColor" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{child.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{childMeta}</div>
                    </div>
                    {!readOnly && (
                      <button
                        type="button"
                        className="cat-editor-child-delete combo-jello-spin"
                        data-tint="red"
                        onClick={() => void handleDeleteChild(child)}
                        onMouseLeave={spinIconOut}
                        title="Delete"
                        aria-label={`Delete ${child.name}`}
                        style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 6, color: '#f87171', cursor: 'pointer', flexShrink: 0 }}
                      >
                        <Trash2 size={13} className="btn-icon" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onSelect(child.id)}
                      className="btn btn-ghost btn-sm combo1"
                      style={{ fontSize: 12, flexShrink: 0 }}
                    >
                      Open <ChevronRight size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      {!readOnly && (
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!deleteOpen ? (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" ref={moveBtnRef} className="btn btn-ghost combo1" onClick={() => setMoveOpen((o) => !o)} onMouseLeave={spinIconOut}>
                <Move size={14} className="btn-icon" /> Move…
              </button>
              {moveOpen && (
                <MoveCategoryPopover
                  anchorRef={moveBtnRef}
                  categories={categories}
                  categoryColorMap={categoryColorMap}
                  categoryId={category.id}
                  categoryName={category.name}
                  currentParentId={category.parent_id}
                  onMove={(id) => void handleMove(id)}
                  onClose={() => setMoveOpen(false)}
                />
              )}
              <button
                type="button"
                className="btn combo3-danger"
                onClick={() => setDeleteOpen(true)}
                onMouseLeave={spinIconOut}
              >
                <Trash2 size={14} className="btn-icon" /> Delete {category.name}
              </button>
            </div>
          ) : (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
                Delete "{category.name}"?
              </div>

              {directItems > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{directItems} item{directItems === 1 ? '' : 's'} in this category will move to</span>
                <select
                  value={deleteMoveTo}
                  onChange={(e) => setDeleteMoveTo(e.target.value === 'uncategorized' ? 'uncategorized' : Number(e.target.value))}
                  style={{ flex: 1, height: 30, background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, padding: '0 8px' }}
                >
                  <option value="uncategorized">Uncategorized</option>
                  {destinationOptions.map((d) => (
                    <option key={d.id} value={d.id}>{'  '.repeat(destinationDepth(d))}{d.name}</option>
                  ))}
                </select>
              </div>
              )}

              {children.length > 0 && (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
                  {children.length} subcategor{children.length === 1 ? 'y' : 'ies'} will move up into {parent ? parent.name : 'the top level'}. The {subtreeItems} item{subtreeItems === 1 ? '' : 's'} inside {children.length === 1 ? 'it' : 'them'} stay{children.length === 1 ? 's' : ''} with {children.length === 1 ? 'that subcategory' : 'those subcategories'}.
                </div>
              )}

              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                This can't be undone.
              </div>

              {hasItemsOrSubs && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                    Type <strong style={{ color: 'var(--text-primary)' }}>{category.name}</strong> to confirm
                  </div>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    className="form-input"
                    style={{ width: '100%' }}
                    autoFocus
                  />
                </div>
              )}

              {error && <div style={{ fontSize: 12, color: 'var(--accent-danger)', marginBottom: 10 }}>{error}</div>}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-ghost combo-cancel-jello"
                  disabled={deleting}
                  onClick={() => { setDeleteOpen(false); setDeleteConfirmText(''); setError(null); }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn combo3-danger"
                  disabled={deleting || (hasItemsOrSubs && deleteConfirmText.trim().toLowerCase() !== category.name.trim().toLowerCase())}
                  onClick={() => void confirmDelete()}
                  onMouseLeave={spinIconOut}
                >
                  <Trash2 size={14} className="btn-icon" />
                  {deleting ? 'Deleting…' : `Delete ${category.name}${directItems > 0 ? ` and move ${directItems} item${directItems === 1 ? '' : 's'}` : ''}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <style>{`
        .cat-editor-child-delete { opacity: 0; transition: opacity 0.15s ease; }
        .cat-editor-child-row:hover .cat-editor-child-delete { opacity: 1; }
        @media (prefers-reduced-motion: reduce) {
          .cat-editor-child-delete { transition: none; }
        }
      `}</style>
      <Toast toast={toast} />
    </div>
  );
}
